import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@material-ui/core'
import { numberWithCommas } from '@openhouse-technologies/utils'
import React from 'react'

import { EMANDATE_METHODS } from 'app/pages/Class/Inventory/constants'

import PaymentBreakup from './PaymentBreakup'

import * as Styles from '../styles'

const formatRupeesFromPaise = (paise) =>
  paise || paise === 0 ? `₹${numberWithCommas(Math.round(paise / 100))}` : '—'

/**
 * "Setup e-Mandate & Book" checkout.
 *
 * Used when the parent has no mandate, or one whose limit is too small for the cart
 * plus their recurring bill. One Razorpay link does both jobs: it authorises the new
 * mandate and collects the prorated first payment. The booking follows the payment,
 * not the mandate — a mandate that ends up rejected does not undo a paid booking.
 */
const SetupEmandateAndBook = ({
  activeOption,
  excelPaymentSummary,
  upcomingExcelPaymentSummary,
  emandateMethod,
  setEmandateMethodHandler,
  reasonForUsingAlfred,
  setReasonForUsingAlfredHandler,
  reasonError,
  showReasonField
}) => {
  const handleReasonChange = (event) => {
    setReasonForUsingAlfredHandler(event.target.value)
  }

  const payableNow = excelPaymentSummary?.find(
    (item) => item.type === 'Total' && item.label === 'Payable Now'
  )
  const monthlyPayable = upcomingExcelPaymentSummary?.find(
    (item) => item.label === 'monthly payable'
  )
  const currentMax = activeOption?.current_max_amount

  return (
    <>
      <Styles.ContentContainer>
        <Styles.CartValue>
          <span>payable now</span>
          <Styles.CoinsContainer>
            <Styles.TotalAmount>
              ₹{numberWithCommas(payableNow?.value ?? 0)}
            </Styles.TotalAmount>
          </Styles.CoinsContainer>
        </Styles.CartValue>

        {/* The first payment is collected by the same link that authorises the mandate,
            so the breakup of that payment belongs here rather than only in the cart. */}
        <PaymentBreakup
          summary={excelPaymentSummary}
          heading="payment breakup"
        />

        <Styles.Subheading>e-mandate to be set up</Styles.Subheading>
        <Styles.PaymentSummary>
          {currentMax ? (
            <Styles.SummaryItem>
              <span>current limit (too low)</span>
              <span>{formatRupeesFromPaise(currentMax)}</span>
            </Styles.SummaryItem>
          ) : null}
          <Styles.SummaryItem>
            <span>new limit</span>
            <span>{formatRupeesFromPaise(activeOption?.max_amount)}</span>
          </Styles.SummaryItem>
          {monthlyPayable ? (
            <Styles.SummaryItem>
              <span>monthly auto-pay from next month</span>
              <span>₹{numberWithCommas(monthlyPayable?.value)}</span>
            </Styles.SummaryItem>
          ) : null}
          <Styles.TotalPayable>
            <span>first payment (now)</span>
            <span>₹{numberWithCommas(payableNow?.value ?? 0)}</span>
          </Styles.TotalPayable>
        </Styles.PaymentSummary>

        {/* Razorpay bakes the rail into the link — a UPI link only offers UPI — so this
            has to be chosen before the link is created, not by the parent afterwards. */}
        <FormControl
          variant="outlined"
          fullWidth
          style={{ marginTop: 16 }}
          size="small"
        >
          <InputLabel id="cart-emandate-method-label">
            Authorisation method
          </InputLabel>
          <Select
            labelId="cart-emandate-method-label"
            label="Authorisation method"
            value={emandateMethod}
            onChange={(e) => setEmandateMethodHandler(e.target.value)}
          >
            {EMANDATE_METHODS.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Styles.Notice>
          A single link sets up the auto-pay and collects the first payment. It
          is sent to the parent on WhatsApp and shown here to copy.{' '}
          <strong>The class is booked once that payment succeeds</strong> — even
          if the mandate itself is still awaiting bank confirmation.
          {currentMax
            ? ' The existing mandate is revoked when the new one is confirmed.'
            : ''}
        </Styles.Notice>
      </Styles.ContentContainer>

      {showReasonField ? (
        <TextField
          name="reason_for_using_alfred"
          label="Reason for using Alfred instead of the App"
          variant="outlined"
          fullWidth
          required
          value={reasonForUsingAlfred}
          onChange={handleReasonChange}
          error={!!reasonError}
          helperText={reasonError}
          style={{ paddingBottom: '10px' }}
        />
      ) : null}
    </>
  )
}

export default SetupEmandateAndBook
